import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { WrappedFieldArrayProps } from 'redux-form';

import type { PreviewVM } from 'ab-viewModels/account/preview.viewModel';

import Dropdown from 'af-components/Controls/Dropdown';
import Pills from 'af-components/Pills';

import * as AccountActions from 'af-actions/accounts';

const modalBody = (original: PreviewVM) => (
	<>
		You are about to remove <b>{original.fullName}</b> from this user group.
		Are you sure you want to continue?
	</>
);

const renderOption = (option: PreviewVM) => <span>{option.fullName} ({option.uniqueId})</span>;

const filterOption = (option: PreviewVM, rawInput: string) => {
	const { fullName, uniqueId } = option;

	return `${fullName} (${uniqueId})`.toLowerCase().includes(rawInput.toLowerCase());
};

const filterOptions = (
	allOptions: PreviewVM[],
	selectedAccounts: PreviewVM[] = []
) => {

	const reduceAccounts = (_acc: Record<number, true>, _account: PreviewVM) => {
		_acc[_account.id] = true;
		return _acc;
	};
	const accounts = selectedAccounts.reduce(reduceAccounts, {});
	return allOptions.filter((_account) => !accounts[_account.id]);
};

type OwnProps = WrappedFieldArrayProps<PreviewVM>;

type Props = OwnProps & ConnectedProps<typeof connector>;

const Accounts: React.FC<Props> = (props) => {
	const {
		fields,
		findAllAccountsForCompany,
	} = props;

	const [accounts, setAccounts] = React.useState<PreviewVM[]>([]);
	const [options, setOptions] = React.useState<PreviewVM[]>([]);

	const initialize = React.useCallback(async () => {
		const _accounts = await findAllAccountsForCompany();
		setAccounts(_accounts);
	}, [findAllAccountsForCompany]);

	// On mount
	React.useEffect(() => {
		initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		setOptions(filterOptions(accounts, fields.getAll()));
	}, [fields, accounts]);

	const removeAccount = React.useCallback((index: number) => {
		fields.remove(index);
	}, [fields]);

	const onChange = React.useCallback((option: PreviewVM) => {
		fields.push(option);
	}, [fields]);

	return (
		<>
			<div>
				<Dropdown<PreviewVM>
					filterable={true}
					filterBy={filterOption}
					forcePlaceholder={true}
					isWhite={true}
					onValueChange={onChange}
					options={options}
					placeholder="Select employees"
					renderMenuItem={renderOption}
					valueKey="id"
					withBorder={true}
					withCaret={true}
				/>
			</div>
			<Pills
				destructiveAction={removeAccount}
				emptyLabel="No users selected"
				items={fields}
				modalBody={modalBody}
				modalTitle="Remove User"
				renderItem={renderOption}
			/>
		</>
	);
};

function mapDispatchToProps() {
	return {
		findAllAccountsForCompany: AccountActions.findAllForCompanyPreview,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(Accounts);
